// @flow
import * as React from 'react';
import ReactModal from 'react-modal';
import ReactDom from 'react-dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import h from 'hyperscript';
import classNames from 'classnames';
import Button from 'components/atoms/button/Button';
import { t } from './withTranslation';
import getTarget from './get-target';

ReactModal.setAppElement(document.getElementById('page'));

type DialogClasses = {
  portalClassName?: string,
  overlayClassName?: string,
  contentClassName?: string,
  innerClassName?: string,
  scrollInnerClassName?: string,
};

type DialogProps = {
  dialogParent: HTMLElement,
  onClose: Function,
  dialogClasses?: DialogClasses,
  trigger?: HTMLElement,
};

function withDialog(WrappedComponent: React.ComponentType<any>) {
  return class Dialog extends React.Component<DialogProps> {
    render(): React.MixedElement {
      const {
        dialogParent,
        onClose,
        trigger,
        dialogClasses: {
          portalClassName,
          overlayClassName,
          contentClassName,
          innerClassName,
          scrollInnerClassName,
        } = {
          portalClassName: undefined,
          overlayClassName: undefined,
          contentClassName: undefined,
          innerClassName: undefined,
          scrollInnerClassName: undefined,
        },
        ...props
      } = this.props;

      return (
        <ReactModal
          isOpen={true}
          onRequestClose={onClose}
          role="dialog"
          parentSelector={() => dialogParent}
          contentLabel="Dialog Overlay"
          portalClassName={classNames('dialog', portalClassName)}
          overlayClassName={classNames('dialog__overlay', overlayClassName)}
          className={classNames('dialog__content', contentClassName)}
        >
          <div className={classNames('dialog__inner', innerClassName)}>
            <div
              className={classNames(
                'dialog__scroll-inner',
                scrollInnerClassName
              )}
            >
              <WrappedComponent
                {...props}
                onCloseDialog={onClose}
                trigger={trigger}
              />
            </div>
          </div>
          <Button
            icon="close"
            iconTitle={t('Schließen')}
            className="button--icon dialog__close"
            style="chromeless"
            onClick={onClose}
          />
        </ReactModal>
      );
    }
  };
}

export default function openDialog(
  dialogSelectorClass: string,
  Component: React.ComponentType<any>,
  dialogClasses?: DialogClasses,
  componentProps?: any
) {
  const selectorClass = dialogSelectorClass.startsWith('.')
    ? dialogSelectorClass.slice(1)
    : dialogSelectorClass;

  document.querySelectorAll(dialogSelectorClass).forEach(($el) => {
    $el.setAttribute('role', 'button');
    $el.setAttribute('tabindex', '0');
    $el.classList.add(`${selectorClass}--initialized`);
    $el.parentElement?.classList.add(`${selectorClass}-parent--initialized`);
  });
  document.addEventListener('click', (event: MouseEvent) => {
    const $target = getTarget(event.target, dialogSelectorClass);

    if ($target) {
      event.preventDefault();
      const $dialogParent = h('#dialog');

      // $FlowFixMe
      document.body.appendChild($dialogParent);

      document.documentElement?.classList.add('has-overlay');
      disableBodyScroll($dialogParent);

      const Dialog = withDialog(Component);

      ReactDom.render(
        <Dialog
          dialogParent={$dialogParent}
          onClose={() => {
            $dialogParent.parentNode.removeChild($dialogParent);
            $target.focus();
            document.documentElement?.classList.remove('has-overlay');
            enableBodyScroll($dialogParent);
          }}
          dialogClasses={dialogClasses}
          trigger={$target}
          {...componentProps}
        />,
        $dialogParent
      );
    }
  });
}
